import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {
  GlobalMessageActions,
  GlobalMessageService,
  GlobalMessageType,
  LoggerService,
  normalizeHttpError,
  SiteContextActions,
  tryNormalizeHttpError,
  withdrawOn
} from '@spartacus/core';

import {CartActions} from '@spartacus/cart/base/core';
import {CartModification} from '@spartacus/cart/base/root';
import {from, Observable} from 'rxjs';
import {catchError, concatMap, map} from 'rxjs/operators';
import {CART_ADD_SPECIAL_REQUESTS, GonvarriCartAddEntry} from "../actions/gonvarri-cart-add-entry.action";
import {GonvarriCartEntryConnector} from '../connectors/gonvarri-cart-entry.connector';
import {GonvarriActiveCartService} from "../services/gonvarri-active-cart.service";
import {GONVARRI_CART_UPDATE_ENTRY, GonvarriCartUpdateEntry} from "../actions/gonvarri-cart-update-entry-success";

@Injectable()
export class GonvarriCartEntryEffects {

  constructor(
    private actions$: Actions,
    private cartEntryConnector: GonvarriCartEntryConnector,
    private activeCartService: GonvarriActiveCartService,
    protected messageService: GlobalMessageService,
    private logger: LoggerService
  ) {}

  private contextChange$ = this.actions$.pipe(
    ofType(
      SiteContextActions.CURRENCY_CHANGE,
      SiteContextActions.LANGUAGE_CHANGE
    )
  );

  addProductWithUnitRequest$: Observable<
    | CartActions.CartAddEntrySuccess
    | CartActions.CartAddEntryFail
    | CartActions.LoadCart
    | GlobalMessageActions.AddMessage
  > = createEffect(() => this.actions$.pipe(
    ofType(CART_ADD_SPECIAL_REQUESTS),
    map((action: GonvarriCartAddEntry) => action.payload),
    concatMap((payload) => {
      return this.cartEntryConnector
        .addEntry(
          payload.userId,
          payload.cartId,
          payload.productCode,
          payload.quantity,
          payload.numberOfSheets,
          payload.packageWeight,
          payload.isContract,
          payload.pickupStore,
          payload.length
        ).pipe(
          map((cartModifification: CartModification) => new CartActions.CartAddEntrySuccess({...payload, ...(cartModifification as Required<CartModification>)})),
          catchError((error) => {
            console.log(error);
            if(error.error.statusCode === "noStock") {
              this.messageService.add({
                key: "quickstock.errors.nostock"
              }, GlobalMessageType.MSG_TYPE_ERROR);
            }
            const actions: Array<
              | CartActions.CartAddEntryFail
              | CartActions.LoadCart
              | GlobalMessageActions.AddMessage
            > = [
              new CartActions.CartAddEntryFail({
                ...payload,
                error: normalizeHttpError(error, this.logger),
              }),
              new CartActions.LoadCart({
                cartId: payload.cartId,
                userId: payload.userId,
              }),
            ];
            error?.error?.errors?.forEach((err: { message: any; }) => {
              if (err.message) {
                actions.push(
                  new GlobalMessageActions.AddMessage({
                    text: { raw: err.message },
                    type: GlobalMessageType.MSG_TYPE_ERROR,
                    timeout: 5000,
                  })
                );
              }
            });
            return from(actions);
          })
        );
    }),
    withdrawOn(this.contextChange$)
  ));

  updateEntry$: Observable<
    | CartActions.CartUpdateEntrySuccess
    | CartActions.CartUpdateEntryFail
    | CartActions.LoadCart
    | GonvarriCartUpdateEntry
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(GONVARRI_CART_UPDATE_ENTRY),
      map((action: GonvarriCartUpdateEntry) => action.payload),
      concatMap((payload) =>
        this.activeCartService.updateEntryPackage(
          Number(payload.entryNumber),
          payload.unitType,
          payload.curQuantity,
          payload.customLength,
          payload.productCode,
          payload.quantity,
          payload.userId,
          payload.cartId,
        )
          .pipe(
            map((data) => {
              let actions: any;
              if(data.statusCode?.includes('success')) {
                actions = new CartActions.CartUpdateEntrySuccess({
                  cartId: payload.cartId,
                  userId: payload.userId,
                  entryNumber: payload.entryNumber,
                  quantity: payload.quantity
                });
                actions.meta.processesCountDiff = 1;
              }else {
                this.handleUpdateEntryError(data);
                actions = [
                  new CartActions.CartUpdateEntryFail({
                    cartId: payload.cartId,
                    userId: payload.userId,
                    entryNumber: payload.entryNumber,
                    quantity: payload.quantity,
                    error: tryNormalizeHttpError(data.statusMessage, this.logger),
                  }),
                  new CartActions.LoadCart({
                    cartId: payload.cartId,
                    userId: payload.userId,
                  })
                ];
              }
              return actions;
            }),
            catchError((error) =>
              from([
                new CartActions.CartUpdateEntryFail({
                  cartId: payload.cartId,
                  userId: payload.userId,
                  entryNumber: payload.entryNumber.toString(),
                  quantity: payload.quantity,
                  error: tryNormalizeHttpError(error, this.logger),
                }),
                new CartActions.LoadCart({
                  cartId: payload.cartId,
                  userId: payload.userId,
                }),
              ])
            )
          )
      ),
      withdrawOn(this.contextChange$),
    )
  );

  addEntry$: Observable<
    | CartActions.CartAddEntrySuccess
    | CartActions.CartAddEntryFail
    | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.CART_ADD_ENTRY),
      map((action: CartActions.CartAddEntry) => action.payload),
      concatMap((payload) => {
        return this.cartEntryConnector
          .add(
            payload.userId,
            payload.cartId,
            payload.productCode,
            payload.quantity,
            payload.pickupStore
          )
          .pipe(
            map(
              (cartModification: CartModification) =>
                new CartActions.CartAddEntrySuccess({
                  ...payload,
                  ...(cartModification as Required<CartModification>),
                })
            ),
            catchError((error) =>
              from([
                new CartActions.CartAddEntryFail({
                  ...payload,
                  error: tryNormalizeHttpError(error, this.logger),
                }),
                new CartActions.LoadCart({
                  cartId: payload.cartId,
                  userId: payload.userId,
                }),
              ])
            )
          );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  removeEntry$: Observable<
    | CartActions.CartRemoveEntrySuccess
    | CartActions.CartRemoveEntryFail
    | CartActions.LoadCart
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.CART_REMOVE_ENTRY),
      map((action: CartActions.CartRemoveEntry) => action.payload),
      concatMap((payload) =>
        this.cartEntryConnector
          .remove(payload.userId, payload.cartId, payload.entryNumber)
          .pipe(
            map(() => {
              let actions: any;
              actions = new CartActions.CartRemoveEntrySuccess({
                ...payload,
              });
              actions.meta = { processesCountDiff: 1 };
              return actions;
            }),
            catchError((error) =>
              from([
                new CartActions.CartRemoveEntryFail({
                  ...payload,
                  error: tryNormalizeHttpError(error, this.logger),
                }),
                new CartActions.LoadCart({
                  cartId: payload.cartId,
                  userId: payload.userId,
                }),
              ])
            )
          )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleUpdateEntryError(data: CartModification): void {
    const messageCode: any = data.statusMessage?.split(" ");
    /* The first part is the translatable part, the second one
    is the quantity min/max received from the backend */
    this.messageService.add(
      { key: messageCode[0], params: { weight: messageCode[1] }},
      GlobalMessageType.MSG_TYPE_ERROR,
      20000
    );
  }

}
